import { FlaskConical } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Chip, KpiTile, Monogram, Note, Tabs, type ChipTone } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { SearchField } from '@/components/ui/search-field'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { PageTitle } from '@/app/layout'
import { errorMessage } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime } from '@/lib/format'
import {
  type ResearchApplication,
  type ResearchList,
  type ResearchMeta,
  type ResearchStatus,
  useResearchAction,
  useResearchProgram,
} from './api'

type View = 'pending' | 'approved' | 'declined' | 'all'

const ROLE_LABEL = { business_owner: 'Business owner', seo_specialist: 'SEO specialist' } as const
const ACCESS_LABEL = {
  own: 'Their own website',
  client_permission: 'Client website, with permission',
} as const
const EXPERIENCE_LABEL = {
  beginner: 'Beginner',
  some: 'Some experience',
  experienced: 'Experienced',
} as const
const PLATFORM_LABEL: Record<string, string> = {
  wordpress: 'WordPress',
  shopify: 'Shopify',
  wix: 'Wix',
  squarespace: 'Squarespace',
  webflow: 'Webflow',
  other: 'Other',
  not_sure: 'Not sure',
}
const STATUS_TONE: Record<ResearchStatus, ChipTone> = {
  pending: 'lime',
  approved: 'ok',
  declined: '',
  revoked: 'warn',
}
const STATUS_LABEL: Record<ResearchStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  revoked: 'Revoked',
}

/**
 * The research program (owner, 2026-11-04): people apply on arvaseo.com, the team approves or
 * declines here. An approved person is invited to sign up with the same email address and gets a
 * year of Starter free once that account's email is confirmed. Owners and admins decide; everyone
 * else reads. Notes are private.
 */
export function ResearchProgramPage() {
  const query = useResearchProgram()

  return (
    <>
      <PageTitle
        title="Research program"
        description="People who applied to test Arva SEO on real websites. Approve someone and they get an email to sign up with the same address; the free year of Starter starts once that email is confirmed."
      />
      {query.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : query.isError ? (
        <div className="card">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : (
        <Board list={query.data} />
      )}
    </>
  )
}

function Board({ list }: { list: ResearchList }) {
  const [view, setView] = useState<View>('pending')
  const [search, setSearch] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const places = list.meta.places
  const count = (v: View) => list.data.filter((a) => v === 'all' || matches(a, v)).length

  const items = useMemo(() => {
    const q = search.trim().toLowerCase()
    return list.data.filter(
      (a) =>
        (view === 'all' || matches(a, view)) &&
        (q === '' ||
          a.name.toLowerCase().includes(q) ||
          (a.business_name ?? '').toLowerCase().includes(q) ||
          a.email.includes(q) ||
          a.websites.some((w) => w.includes(q))),
    )
  }, [list.data, view, search])
  const open = list.data.find((a) => a.id === openId) ?? null

  return (
    <div className="sec" style={{ gap: 20 }}>
      <div className="kpis">
        <KpiTile
          label="Business owners"
          value={`${places.business_owner.taken} of ${places.business_owner.slots}`}
          compare={`approved · ${places.business_owner.pending} waiting`}
        />
        <KpiTile
          label="SEO specialists"
          value={`${places.seo_specialist.taken} of ${places.seo_specialist.slots}`}
          compare={`approved · ${places.seo_specialist.pending} waiting`}
        />
        <KpiTile
          label="Free year applied"
          value={String(list.data.filter((a) => a.grant_applied_at !== null).length)}
          compare="accounts with the same email, confirmed"
        />
        <KpiTile
          label="Applications"
          value={String(list.data.length)}
          compare={list.meta.open ? 'open' : 'closed'}
        />
      </div>

      <Tabs<View>
        label="Which applications"
        value={view}
        onChange={setView}
        tabs={[
          { key: 'pending', label: 'Pending', count: count('pending'), alert: count('pending') > 0 },
          { key: 'approved', label: 'Approved', count: count('approved') },
          { key: 'declined', label: 'Declined', count: count('declined') },
          { key: 'all', label: 'All', count: count('all') },
        ]}
      />
      <div>
        <SearchField
          label="Search applications"
          placeholder="Search name, email or website"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {items.length === 0 ? (
        <Note icon={<FlaskConical aria-hidden="true" />} title="Nothing here" calm>
          {list.data.length === 0
            ? 'No applications yet. They arrive from the research program page on arvaseo.com.'
            : 'No applications match this view.'}
        </Note>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="t" aria-label="Research program applications">
            <thead>
              <tr>
                <th>Name</th>
                <th>Business</th>
                <th>Role</th>
                <th>Websites</th>
                <th>Access</th>
                <th>Country, platform, industry</th>
                <th>Experience</th>
                <th>Applied</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="click" onClick={() => setOpenId(a.id)}>
                  <td>
                    <div className="who">
                      <Monogram name={a.name} size="sm" />
                      <div>
                        <b>
                          <button
                            type="button"
                            className="text-left font-semibold hover:underline"
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenId(a.id)
                            }}
                          >
                            {a.name}
                          </button>
                        </b>
                        <small>{a.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>{a.business_name ?? ''}</td>
                  <td>{ROLE_LABEL[a.role]}</td>
                  <td>{a.websites.join(', ')}</td>
                  <td>{a.website_access === 'own' ? 'Own' : 'Client'}</td>
                  <td>
                    {a.country} · {PLATFORM_LABEL[a.platform] ?? a.platform} · {a.industry}
                  </td>
                  <td>{EXPERIENCE_LABEL[a.experience]}</td>
                  <td title={formatDateTime(a.created_at)}>{formatDate(a.created_at)}</td>
                  <td>
                    <Chip tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <Detail application={open} meta={list.meta} onClose={() => setOpenId(null)} />}
    </div>
  )
}

function matches(a: ResearchApplication, v: View): boolean {
  // Revoked people sit with Declined: they are no longer in the program.
  return v === 'declined' ? a.status === 'declined' || a.status === 'revoked' : a.status === v
}

type Ask = 'approve' | 'decline' | 'revoke' | null

/** Everything they answered, how far an approved person has got, and the actions. */
function Detail({
  application: a,
  meta,
  onClose,
}: {
  application: ResearchApplication
  meta: ResearchMeta
  onClose: () => void
}) {
  const [ask, setAsk] = useState<Ask>(null)
  const act = useResearchAction()
  const fail = (e: unknown) => toast.error(errorMessage(e))
  const places = meta.places[a.role]

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          title={a.name}
          description={`${a.email} · applied ${formatDate(a.created_at)}`}
          className="top-0 right-0 left-auto h-dvh max-h-dvh w-full max-w-xl translate-x-0 translate-y-0 rounded-none border-y-0 border-r-0"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Chip>
            <Chip tone="line">{ROLE_LABEL[a.role]}</Chip>
            <Chip tone="line">{ACCESS_LABEL[a.website_access]}</Chip>
          </div>

          <dl className="grid gap-3 text-[13.5px]">
            <Row label="Business or company">{a.business_name ?? 'Not given'}</Row>
            <Row label="Websites">
              <ul className="grid gap-1">
                {a.websites.map((w) => (
                  <li key={w}>
                    <a href={`https://${w}`} target="_blank" rel="noreferrer noopener">
                      {w}
                    </a>
                  </li>
                ))}
              </ul>
            </Row>
            <Row label="Country">{a.country}</Row>
            <Row label="Platform">{PLATFORM_LABEL[a.platform] ?? a.platform}</Row>
            <Row label="Industry">{a.industry}</Row>
            <Row label="Experience">{EXPERIENCE_LABEL[a.experience]}</Row>
            <Row label="What they want to learn or improve">
              <p className="whitespace-pre-line">{a.goals}</p>
            </Row>
            <Row label="Agreed">
              Can give about 2 hours a week for the first month and answer short surveys. Accepted the terms
              of {a.terms_version} on {formatDateTime(a.terms_accepted_at)}.
            </Row>
            {a.staff_note && (
              <Row label="Team note (private)">
                <p className="whitespace-pre-line">{a.staff_note}</p>
              </Row>
            )}
          </dl>

          {a.status === 'approved' && <Progress a={a} />}

          {meta.can_decide ? (
            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              {(a.status === 'pending' || a.status === 'declined' || a.status === 'revoked') && (
                <Button onClick={() => setAsk('approve')}>Approve</Button>
              )}
              {a.status === 'pending' && (
                <Button variant="secondary" onClick={() => setAsk('decline')}>
                  Decline
                </Button>
              )}
              {a.status === 'approved' && a.grant_applied_at === null && (
                <>
                  <Button
                    variant="secondary"
                    disabled={act.isPending}
                    onClick={() =>
                      act.mutate(
                        { id: a.id, action: 'resend' },
                        { onSuccess: () => toast.success(`Invite sent again to ${a.email}.`), onError: fail },
                      )
                    }
                  >
                    Resend invite
                  </Button>
                  <Button variant="ghost" onClick={() => setAsk('revoke')}>
                    Revoke
                  </Button>
                </>
              )}
            </div>
          ) : (
            <p className="border-t border-border pt-4 text-[13px] text-ink-subtle">
              Owners and admins approve and decline.
            </p>
          )}
        </DialogContent>
      </Dialog>

      {ask === 'approve' && (
        <DecisionDialog
          title={`Approve ${a.name}?`}
          description={
            places.taken >= places.slots
              ? `All ${places.slots} places for ${a.role === 'seo_specialist' ? 'SEO specialists' : 'business owners'} are taken. Raise the limit in settings first.`
              : `This sends an email to ${a.email} and uses one of ${places.slots} places.`
          }
          action="Approve and send invite"
          pending={act.isPending}
          onClose={() => setAsk(null)}
          onSubmit={(note) =>
            act.mutate(
              { id: a.id, action: 'approve', note },
              {
                onSuccess: () => {
                  toast.success(`Approved. The invite was sent to ${a.email}.`)
                  setAsk(null)
                },
                onError: fail,
              },
            )
          }
        >
          <dl className="grid gap-2 rounded-md bg-surface-sunken p-3 text-[13px]">
            <Row label="Email subject">{meta.invite_subject}</Row>
            <Row label="Account email (must match)">{a.email}</Row>
          </dl>
        </DecisionDialog>
      )}
      {ask === 'decline' && (
        <DecisionDialog
          title={`Decline ${a.name}?`}
          description="They are not told why."
          action="Decline"
          pending={act.isPending}
          onClose={() => setAsk(null)}
          notifyLabel="Email them a short thank-you"
          onSubmit={(note, notify) =>
            act.mutate(
              { id: a.id, action: 'decline', note, notify },
              {
                onSuccess: () => {
                  toast.success(notify ? 'Declined. A thank-you was sent.' : 'Declined. No email was sent.')
                  setAsk(null)
                },
                onError: fail,
              },
            )
          }
        />
      )}
      {ask === 'revoke' && (
        <DecisionDialog
          title={`Revoke ${a.name}’s place?`}
          description="Their place is given back. This only works before the free year is applied."
          action="Revoke place"
          pending={act.isPending}
          onClose={() => setAsk(null)}
          onSubmit={(note) =>
            act.mutate(
              { id: a.id, action: 'revoke', note },
              {
                onSuccess: () => {
                  toast.success('Place revoked.')
                  setAsk(null)
                },
                onError: fail,
              },
            )
          }
        />
      )}
    </>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-ink-subtle">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  )
}

/** Invited, account created, email verified, free year applied. */
function Progress({ a }: { a: ResearchApplication }) {
  const steps: { label: string; at: string | null }[] = [
    { label: a.invite_count > 1 ? `Invited (${a.invite_count} times)` : 'Invited', at: a.invited_at },
    { label: 'Account created', at: a.account_created_at },
    { label: 'Email verified', at: a.account_verified_at },
    { label: 'Free year applied', at: a.grant_applied_at },
  ]
  return (
    <section className="grid gap-2 border-t border-border pt-4" aria-label="Progress">
      <h3 className="text-sm font-semibold text-ink">Progress</h3>
      <ol className="grid gap-1.5 text-[13px]">
        {steps.map((s) => (
          <li key={s.label} className={cn('flex justify-between gap-3', s.at === null && 'text-ink-subtle')}>
            <span>
              {s.at !== null ? '✓ ' : '– '}
              {s.label}
            </span>
            <span>{s.at !== null ? formatDate(s.at) : 'not yet'}</span>
          </li>
        ))}
      </ol>
      {a.workspace && (
        <p className="text-[13px]">
          <Link to={`/customers/${a.workspace.id}`}>Open {a.workspace.name} on the customer page</Link>
        </p>
      )}
    </section>
  )
}

function DecisionDialog({
  title,
  description,
  action,
  pending,
  notifyLabel,
  children,
  onClose,
  onSubmit,
}: {
  title: string
  description: string
  action: string
  pending: boolean
  /** A checkbox, ticked to start with. */
  notifyLabel?: string
  children?: React.ReactNode
  onClose: () => void
  onSubmit: (note: string | null, notify: boolean) => void
}) {
  const [note, setNote] = useState('')
  const [notify, setNotify] = useState(true)
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={title}
        description={description}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button disabled={pending} onClick={() => onSubmit(note.trim() || null, notify)}>
              {action}
            </Button>
          </>
        }
      >
        {children}
        {notifyLabel && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            {notifyLabel}
          </label>
        )}
        <Field label="Private note" htmlFor="research-note" hint="Only the team sees it.">
          <Textarea
            id="research-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            rows={3}
          />
        </Field>
      </DialogContent>
    </Dialog>
  )
}
